/**
 * Rule metadata for surfacing the rule set to the GM (the §5.2.1
 * severity-override settings panel). The deterministic rules live in
 * `./rules/`; this is the human-/UI-facing catalogue: each rule's id and its
 * engine-default severity. Kept separate from the rule functions so a
 * settings surface can enumerate rules without importing the audit logic.
 *
 * `message_key` mirrors the i18n key each rule emits, so a UI can render a
 * friendly rule name from the same catalogue the client already localises.
 */
import type { Severity } from './advisory.js';

export interface RuleMeta {
  id: string;
  /** The engine-default severity when no Campaign override applies. */
  defaultSeverity: Severity;
  /** The i18n message key the rule emits (shared with the advisory render). */
  messageKey: string;
}

/** The v1 rule catalogue, in display order (most-severe defaults first). */
export const AUDITOR_RULES: readonly RuleMeta[] = [
  {
    id: 'containment.cycle',
    defaultSeverity: 'blocking_error',
    messageKey: 'auditor.containment.cycle',
  },
  { id: 'ref.dangling', defaultSeverity: 'error', messageKey: 'auditor.ref.dangling' },
  { id: 'causal.cycle', defaultSeverity: 'error', messageKey: 'auditor.causal.cycle' },
  { id: 'ref.wrong_type', defaultSeverity: 'warning', messageKey: 'auditor.ref.wrong_type' },
  {
    id: 'predicate.value_kind_mismatch',
    defaultSeverity: 'warning',
    messageKey: 'auditor.predicate.value_kind_mismatch',
  },
  {
    id: 'predicate.cardinality_violation',
    defaultSeverity: 'warning',
    messageKey: 'auditor.predicate.cardinality_violation',
  },
  {
    id: 'belief.contradicts_canon',
    defaultSeverity: 'info',
    messageKey: 'auditor.belief.contradicts_canon',
  },
  {
    id: 'recurrence.open_cycle',
    defaultSeverity: 'info',
    messageKey: 'auditor.recurrence.open_cycle',
  },
  {
    id: 'cursor.visibility.long_browse',
    defaultSeverity: 'info',
    messageKey: 'auditor.cursor_visibility.long_browse',
  },
  {
    id: 'cursor.visibility.map_hidden',
    defaultSeverity: 'info',
    messageKey: 'auditor.cursor_visibility.map_hidden',
  },
  {
    id: 'cursor.spoof.active',
    defaultSeverity: 'info',
    messageKey: 'auditor.cursor_spoof.active',
  },
  {
    id: 'facilitation.mode_mismatch',
    defaultSeverity: 'info',
    messageKey: 'auditor.facilitation.mode_mismatch',
  },
  {
    id: 'combat.quick_log_unreviewed',
    defaultSeverity: 'info',
    messageKey: 'auditor.combat.quick_log_unreviewed',
  },
];
